import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import "../css/customSelect.css";

const normalizeOption = (opt) => {
  if (typeof opt === "object" && opt !== null) {
    const value = String(opt.value ?? "");
    return {
      value,
      label: opt.label ?? value,
      disabled: Boolean(opt.disabled),
    };
  }
  const value = String(opt);
  return { value, label: value, disabled: false };
};

/**
 * Custom dropdown; onChange matches native select: (e) => e.target.value
 */
export default function CustomSelect({
  value,
  onChange,
  options = [],
  className = "",
  triggerClassName = "",
  menuPlacement = "bottom",
  fullWidth = false,
  disabled = false,
  id,
  "aria-label": ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const normalized = useMemo(() => options.map(normalizeOption), [options]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const currentValue = String(value ?? "");
  const selected =
    normalized.find((o) => o.value === currentValue) || normalized[0];

  const pick = (next) => {
    if (onChange) onChange({ target: { value: String(next) } });
    close();
  };

  const rootClass = [
    "app-custom-select",
    fullWidth ? "app-custom-select--full" : "",
    menuPlacement === "top" ? "app-custom-select--menu-top" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass} ref={rootRef} id={id}>
      <button
        type="button"
        className={`app-custom-select__trigger ${triggerClassName}`.trim()}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="app-custom-select__label">
          {selected?.label ?? ""}
        </span>
        <svg
          className={`app-custom-select__chev ${open ? "is-open" : ""}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul className="app-custom-select__menu" role="listbox">
          {normalized.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === currentValue}
              aria-disabled={opt.disabled}
              className={[
                opt.value === currentValue ? "is-active" : "",
                opt.disabled ? "is-disabled" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => !opt.disabled && pick(opt.value)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
